// ADR-002 / P1: Lazy dependency injection — dependencies are constructed on first access, not at container init.
import {
  AuthRepository,
  type IAuthRepository,
} from "../repositories/AuthRepository";
import {
  StudyRepository,
  type IStudyRepository,
} from "../repositories/StudyRepository";
import {
  UserRepository,
  type IUserRepository,
} from "../repositories/UserRepository";
import {
  SiteRepository,
  type ISiteRepository,
} from "@/repositories/SiteRepository";
import {
  StudySiteAssignmentRepository,
  type IStudySiteAssignmentRepository,
} from "@/repositories/StudySiteAssignmentRepository";
import {
  RoleRepository,
  type IRoleRepository,
} from "@/repositories/RoleRepository";
import {
  MilestoneRepository,
  type IMilestoneRepository,
} from "@/repositories/MilestoneRepository";
import {
  MonitoringOversightRepository,
  type IMonitoringOversightRepository,
} from "@/repositories/MonitoringOversightRepository";

import { LoginUseCase, type ILoginUseCase } from "../usecases/LoginUseCase";
import {
  ForgotPasswordUseCase,
  type IForgotPasswordUseCase,
} from "../usecases/ForgotPasswordUseCase";
import {
  ResetPasswordUseCase,
  type IResetPasswordUseCase,
} from "../usecases/ResetPasswordUseCase";
import {
  ChangePasswordUseCase,
  type IChangePasswordUseCase,
} from "../usecases/ChangePasswordUseCase";
import {
  ChangeExpiredPasswordUseCase,
  type IChangeExpiredPasswordUseCase,
} from "../usecases/ChangeExpiredPasswordUseCase";
import { StudyUseCase, type IStudyUseCase } from "../usecases/StudyUseCase";
import { UserUseCase, type IUserUseCase } from "../usecases/UserUseCase";
import { SiteUseCase, type ISiteUseCase } from "../usecases/SiteUseCase";
import {
  StudySiteAssignmentUseCase,
  type IStudySiteAssignmentUseCase,
} from "../usecases/StudySiteAssignmentUseCase";
import {
  MonitoringVisitRepository,
  type IMonitoringVisitRepository,
} from "../repositories/MonitoringVisitRepository";
import {
  MonitoringTaskRepository,
  type IMonitoringTaskRepository,
} from "../repositories/MonitoringTaskRepository";
import {
  MonitoringVisitUseCase,
  type IMonitoringVisitUseCase,
} from "../usecases/MonitoringVisitUseCase";
import {
  MonitoringTaskUseCase,
  type IMonitoringTaskUseCase,
} from "../usecases/MonitoringTaskUseCase";
import { RoleUseCase, type IRoleUseCase } from "../usecases/RoleUseCase";
import {
  MilestoneUseCase,
  type IMilestoneUseCase,
} from "../usecases/MilestoneUseCase";
import {
  MonitoringOversightUseCase,
  type IMonitoringOversightUseCase,
} from "../usecases/MonitoringOversightUseCase";
import {
  SiteActivationRepository,
  type ISiteActivationRepository,
} from "../repositories/SiteActivationRepository";
import {
  SiteActivationUseCase,
  type ISiteActivationUseCase,
} from "../usecases/SiteActivationUseCase";
import {
  RegulatoryRepository,
  type IRegulatoryRepository,
} from "@/repositories/RegulatoryRepository";
import {
  RegulatoryUseCase,
  type IRegulatoryUseCase,
} from "@/usecases/RegulatoryUseCase";
import {
  VisitAttachmentRepository,
  type IVisitAttachmentRepository,
} from "@/repositories/VisitAttachmentRepository";
import {
  VisitAttachmentUseCase,
  type IVisitAttachmentUseCase,
} from "@/usecases/VisitAttachmentUseCase";
import {
  AuditLogRepository,
  type IAuditLogRepository,
} from "@/repositories/AuditLogRepository";
import {
  AuditLogUseCase,
  type IAuditLogUseCase,
} from "@/usecases/AuditLogUseCase";
import {
  FinanceRepository,
  type IFinanceRepository,
} from "@/repositories/FinanceRepository";
import {
  FinanceUseCase,
  type IFinanceUseCase,
} from "@/usecases/FinanceUseCase";
import {
  ETMFRepository,
  type IETMFRepository,
} from "@/repositories/ETMFRepository";
import { ETMFUseCase, type IETMFUseCase } from "@/usecases/ETMFUseCase";
import {
  EnrollmentRepository,
  type IEnrollmentRepository,
} from "../repositories/EnrollmentRepository";
import {
  EnrollmentUseCase,
  type IEnrollmentUseCase,
} from "../usecases/EnrollmentUseCase";
import {
  RiskRepository,
  type IRiskRepository,
} from "../repositories/RiskRepository";
import { RiskUseCase, type IRiskUseCase } from "../usecases/RiskUseCase";
import {
  SiteFeasibilityRepository,
  type ISiteFeasibilityRepository,
} from "../repositories/SiteFeasibilityRepository";
import {
  SiteFeasibilityUseCase,
  type ISiteFeasibilityUseCase,
} from "../usecases/SiteFeasibilityUseCase";
import {
  ConnectorRepository,
  type IConnectorRepository,
} from "../repositories/ConnectorRepository";
import {
  ConnectorUseCase,
  type IConnectorUseCase,
} from "../usecases/ConnectorUseCase";
import {
  DashboardRepository,
  type IDashboardRepository,
} from "@/repositories/DashboardRepository";
import {
  DashboardUseCase,
  type IDashboardUseCase,
} from "@/usecases/DashboardUseCase";
import {
  ExportRepository,
  type IExportRepository,
} from "@/repositories/ExportRepository";
import { ExportUseCase, type IExportUseCase } from "@/usecases/ExportUseCase";
import {
  ReportsRepository,
  type IReportsRepository,
} from "@/repositories/ReportsRepository";
import {
  ReportsUseCase,
  type IReportsUseCase,
} from "@/usecases/ReportsUseCase";
import {
  CtmsTaskRepository,
  type CtmsTaskRepository as ICtmsTaskRepository,
} from "@/repositories/CtmsTaskRepository";
import { CtmsTaskUseCase } from "@/usecases/CtmsTaskUseCase";
import {
  CDARepository,
  type ICDARepository,
} from "../repositories/CDARepository";
import { CDAUseCase, type ICDAUseCase } from "../usecases/CDAUseCase";
import {
  PrincipalInvestigatorRepository,
  type IPrincipalInvestigatorRepository,
} from "../repositories/PrincipalInvestigatorRepository";
import {
  InvestigatorUseCase,
  type IInvestigatorUseCase,
} from "../usecases/InvestigatorUseCase";
import {
  RBQMRepository,
  type IRBQMRepository,
} from "../repositories/RBQMRepository";
import { RBQMUseCase, type IRBQMUseCase } from "../usecases/RBQMUseCase";
import {
  QMSRepository,
  type IQMSRepository,
} from "../repositories/QMSRepository";
import { QMSUseCase, type IQMSUseCase } from "../usecases/QMSUseCase";
import { HttpCoreService } from "../services/HttpCoreService";

export class DependencyContainer {
  private static instance: DependencyContainer;

  // Repositories
  private _authRepository: IAuthRepository | null = null;
  private _studyRepository: IStudyRepository | null = null;
  private _userRepository: IUserRepository | null = null;
  private _siteRepository: ISiteRepository | null = null;
  private _studySiteAssignmentRepository: IStudySiteAssignmentRepository | null =
    null;
  private _monitoringVisitRepository: IMonitoringVisitRepository | null = null;
  private _monitoringTaskRepository: IMonitoringTaskRepository | null = null;
  private _roleRepository: IRoleRepository | null = null;
  private _milestoneRepository: IMilestoneRepository | null = null;
  private _monitoringOversightRepository: IMonitoringOversightRepository | null =
    null;
  private _siteActivationRepository: ISiteActivationRepository | null = null;
  private _regulatoryRepository: IRegulatoryRepository | null = null;
  private _visitAttachmentRepository: IVisitAttachmentRepository | null = null;
  private _auditLogRepository: IAuditLogRepository | null = null;
  private _financeRepository: IFinanceRepository | null = null;
  private _etmfRepository: IETMFRepository | null = null;
  private _dashboardRepository: IDashboardRepository | null = null;
  private _exportRepository: IExportRepository | null = null;
  private _reportsRepository: IReportsRepository | null = null;
  private _ctmsTaskRepository: ICtmsTaskRepository | null = null;
  private _enrollmentRepository: IEnrollmentRepository | null = null;
  private _riskRepository: IRiskRepository | null = null;
  private _siteFeasibilityRepository: ISiteFeasibilityRepository | null = null;
  private _cdaRepository: ICDARepository | null = null;
  private _connectorRepository: IConnectorRepository | null = null;
  private _piRepository: IPrincipalInvestigatorRepository | null = null;
  private _rbqmRepository: IRBQMRepository | null = null;
  private _qmsRepository: IQMSRepository | null = null;

  // Use Cases
  private _loginUseCase: ILoginUseCase | null = null;
  private _forgotPasswordUseCase: IForgotPasswordUseCase | null = null;
  private _resetPasswordUseCase: IResetPasswordUseCase | null = null;
  private _changePasswordUseCase: IChangePasswordUseCase | null = null;
  private _changeExpiredPasswordUseCase: IChangeExpiredPasswordUseCase | null =
    null;
  private _studyUseCase: IStudyUseCase | null = null;
  private _userUseCase: IUserUseCase | null = null;
  private _siteUseCase: ISiteUseCase | null = null;
  private _studySiteAssignmentUseCase: IStudySiteAssignmentUseCase | null =
    null;
  private _monitoringVisitUseCase: IMonitoringVisitUseCase | null = null;
  private _monitoringTaskUseCase: IMonitoringTaskUseCase | null = null;
  private _roleUseCase: IRoleUseCase | null = null;
  private _milestoneUseCase: IMilestoneUseCase | null = null;
  private _monitoringOversightUseCase: IMonitoringOversightUseCase | null =
    null;
  private _siteActivationUseCase: ISiteActivationUseCase | null = null;
  private _regulatoryUseCase: IRegulatoryUseCase | null = null;
  private _auditLogUseCase: IAuditLogUseCase | null = null;
  private _visitAttachmentUseCase: IVisitAttachmentUseCase | null = null;
  private _financeUseCase: IFinanceUseCase | null = null;
  private _etmfUseCase: IETMFUseCase | null = null;
  private _dashboardUseCase: IDashboardUseCase | null = null;
  private _exportUseCase: IExportUseCase | null = null;
  private _reportsUseCase: IReportsUseCase | null = null;
  private _ctmsTaskUseCase: CtmsTaskUseCase | null = null;
  private _enrollmentUseCase: IEnrollmentUseCase | null = null;
  private _riskUseCase: IRiskUseCase | null = null;
  private _siteFeasibilityUseCase: ISiteFeasibilityUseCase | null = null;
  private _cdaUseCase: ICDAUseCase | null = null;
  private _connectorUseCase: IConnectorUseCase | null = null;
  private _investigatorUseCase: IInvestigatorUseCase | null = null;
  private _rbqmUseCase: IRBQMUseCase | null = null;
  private _qmsUseCase: IQMSUseCase | null = null;

  private constructor() {
    // Singleton only — dependencies are created lazily via getters (ADR-002).
  }

  public static getInstance(): DependencyContainer {
    if (!DependencyContainer.instance) {
      DependencyContainer.instance = new DependencyContainer();
    }
    return DependencyContainer.instance;
  }

  // Getters for dependencies
  get authRepository(): IAuthRepository {
    this._authRepository ??= new AuthRepository();
    return this._authRepository;
  }

  get studyRepository(): IStudyRepository {
    this._studyRepository ??= new StudyRepository();
    return this._studyRepository;
  }

  get userRepository(): IUserRepository {
    this._userRepository ??= new UserRepository();
    return this._userRepository;
  }

  get siteRepository(): ISiteRepository {
    this._siteRepository ??= new SiteRepository();
    return this._siteRepository;
  }

  get loginUseCase(): ILoginUseCase {
    this._loginUseCase ??= new LoginUseCase(this.authRepository);
    return this._loginUseCase;
  }

  get forgotPasswordUseCase(): IForgotPasswordUseCase {
    this._forgotPasswordUseCase ??= new ForgotPasswordUseCase(
      this.authRepository,
    );
    return this._forgotPasswordUseCase;
  }

  get resetPasswordUseCase(): IResetPasswordUseCase {
    this._resetPasswordUseCase ??= new ResetPasswordUseCase(
      this.authRepository,
    );
    return this._resetPasswordUseCase;
  }

  get changePasswordUseCase(): IChangePasswordUseCase {
    this._changePasswordUseCase ??= new ChangePasswordUseCase(
      this.authRepository,
    );
    return this._changePasswordUseCase;
  }

  get changeExpiredPasswordUseCase(): IChangeExpiredPasswordUseCase {
    this._changeExpiredPasswordUseCase ??= new ChangeExpiredPasswordUseCase(
      this.authRepository,
    );
    return this._changeExpiredPasswordUseCase;
  }

  get userUseCase(): IUserUseCase {
    this._userUseCase ??= new UserUseCase(this.userRepository);
    return this._userUseCase;
  }

  get studyUseCase(): IStudyUseCase {
    this._studyUseCase ??= new StudyUseCase(this.studyRepository);
    return this._studyUseCase;
  }

  get siteUseCase(): ISiteUseCase {
    this._siteUseCase ??= new SiteUseCase(this.siteRepository);
    return this._siteUseCase;
  }

  get studySiteAssignmentUseCase(): IStudySiteAssignmentUseCase {
    this._studySiteAssignmentUseCase ??= new StudySiteAssignmentUseCase(
      this.studySiteAssignmentRepository,
    );
    return this._studySiteAssignmentUseCase;
  }

  get studySiteAssignmentRepository(): IStudySiteAssignmentRepository {
    this._studySiteAssignmentRepository ??= new StudySiteAssignmentRepository();
    return this._studySiteAssignmentRepository;
  }

  get monitoringVisitRepository(): IMonitoringVisitRepository {
    this._monitoringVisitRepository ??= new MonitoringVisitRepository();
    return this._monitoringVisitRepository;
  }

  get monitoringVisitUseCase(): IMonitoringVisitUseCase {
    this._monitoringVisitUseCase ??= new MonitoringVisitUseCase(
      this.monitoringVisitRepository,
    );
    return this._monitoringVisitUseCase;
  }

  get monitoringTaskRepository(): IMonitoringTaskRepository {
    this._monitoringTaskRepository ??= new MonitoringTaskRepository();
    return this._monitoringTaskRepository;
  }

  get monitoringTaskUseCase(): IMonitoringTaskUseCase {
    this._monitoringTaskUseCase ??= new MonitoringTaskUseCase(
      this.monitoringTaskRepository,
    );
    return this._monitoringTaskUseCase;
  }

  get roleRepository(): IRoleRepository {
    this._roleRepository ??= new RoleRepository();
    return this._roleRepository;
  }

  get roleUseCase(): IRoleUseCase {
    this._roleUseCase ??= new RoleUseCase(this.roleRepository);
    return this._roleUseCase;
  }

  get milestoneRepository(): IMilestoneRepository {
    this._milestoneRepository ??= new MilestoneRepository();
    return this._milestoneRepository;
  }

  get milestoneUseCase(): IMilestoneUseCase {
    this._milestoneUseCase ??= new MilestoneUseCase(this.milestoneRepository);
    return this._milestoneUseCase;
  }

  get monitoringOversightRepository(): IMonitoringOversightRepository {
    this._monitoringOversightRepository ??= new MonitoringOversightRepository();
    return this._monitoringOversightRepository;
  }

  get monitoringOversightUseCase(): IMonitoringOversightUseCase {
    this._monitoringOversightUseCase ??= new MonitoringOversightUseCase(
      this.monitoringOversightRepository,
    );
    return this._monitoringOversightUseCase;
  }

  get siteActivationRepository(): ISiteActivationRepository {
    this._siteActivationRepository ??= new SiteActivationRepository(
      new HttpCoreService(),
    );
    return this._siteActivationRepository;
  }

  get siteActivationUseCase(): ISiteActivationUseCase {
    this._siteActivationUseCase ??= new SiteActivationUseCase(
      this.siteActivationRepository,
    );
    return this._siteActivationUseCase;
  }

  get regulatoryRepository(): IRegulatoryRepository {
    this._regulatoryRepository ??= new RegulatoryRepository(
      new HttpCoreService(),
    );
    return this._regulatoryRepository;
  }

  get regulatoryUseCase(): IRegulatoryUseCase {
    this._regulatoryUseCase ??= new RegulatoryUseCase(
      this.regulatoryRepository,
    );
    return this._regulatoryUseCase;
  }

  get visitAttachmentRepository(): IVisitAttachmentRepository {
    this._visitAttachmentRepository ??= new VisitAttachmentRepository();
    return this._visitAttachmentRepository;
  }

  get visitAttachmentUseCase(): IVisitAttachmentUseCase {
    this._visitAttachmentUseCase ??= new VisitAttachmentUseCase(
      this.visitAttachmentRepository,
    );
    return this._visitAttachmentUseCase;
  }

  get auditLogRepository(): IAuditLogRepository {
    this._auditLogRepository ??= new AuditLogRepository();
    return this._auditLogRepository;
  }

  get auditLogUseCase(): IAuditLogUseCase {
    this._auditLogUseCase ??= new AuditLogUseCase(this.auditLogRepository);
    return this._auditLogUseCase;
  }

  get financeRepository(): IFinanceRepository {
    this._financeRepository ??= new FinanceRepository();
    return this._financeRepository;
  }

  get financeUseCase(): IFinanceUseCase {
    this._financeUseCase ??= new FinanceUseCase(this.financeRepository);
    return this._financeUseCase;
  }

  get etmfRepository(): IETMFRepository {
    this._etmfRepository ??= new ETMFRepository(new HttpCoreService());
    return this._etmfRepository;
  }

  get etmfUseCase(): IETMFUseCase {
    this._etmfUseCase ??= new ETMFUseCase(this.etmfRepository);
    return this._etmfUseCase;
  }

  get dashboardRepository(): IDashboardRepository {
    this._dashboardRepository ??= new DashboardRepository();
    return this._dashboardRepository;
  }

  get dashboardUseCase(): IDashboardUseCase {
    this._dashboardUseCase ??= new DashboardUseCase(this.dashboardRepository);
    return this._dashboardUseCase;
  }

  get exportRepository(): IExportRepository {
    this._exportRepository ??= new ExportRepository();
    return this._exportRepository;
  }

  get exportUseCase(): IExportUseCase {
    this._exportUseCase ??= new ExportUseCase(this.exportRepository);
    return this._exportUseCase;
  }

  get reportsRepository(): IReportsRepository {
    this._reportsRepository ??= new ReportsRepository();
    return this._reportsRepository;
  }

  get reportsUseCase(): IReportsUseCase {
    this._reportsUseCase ??= new ReportsUseCase(this.reportsRepository);
    return this._reportsUseCase;
  }

  get ctmsTaskRepository(): ICtmsTaskRepository {
    this._ctmsTaskRepository ??= new CtmsTaskRepository();
    return this._ctmsTaskRepository;
  }

  get ctmsTaskUseCase(): CtmsTaskUseCase {
    this._ctmsTaskUseCase ??= new CtmsTaskUseCase(this.ctmsTaskRepository);
    return this._ctmsTaskUseCase;
  }

  get enrollmentRepository(): IEnrollmentRepository {
    this._enrollmentRepository ??= new EnrollmentRepository();
    return this._enrollmentRepository;
  }

  get enrollmentUseCase(): IEnrollmentUseCase {
    this._enrollmentUseCase ??= new EnrollmentUseCase(
      this.enrollmentRepository,
    );
    return this._enrollmentUseCase;
  }

  get riskRepository(): IRiskRepository {
    this._riskRepository ??= new RiskRepository();
    return this._riskRepository;
  }

  get riskUseCase(): IRiskUseCase {
    this._riskUseCase ??= new RiskUseCase(this.riskRepository);
    return this._riskUseCase;
  }

  get siteFeasibilityRepository(): ISiteFeasibilityRepository {
    this._siteFeasibilityRepository ??= new SiteFeasibilityRepository(
      new HttpCoreService(),
    );
    return this._siteFeasibilityRepository;
  }

  get siteFeasibilityUseCase(): ISiteFeasibilityUseCase {
    this._siteFeasibilityUseCase ??= new SiteFeasibilityUseCase(
      this.siteFeasibilityRepository,
    );
    return this._siteFeasibilityUseCase;
  }

  get cdaRepository(): ICDARepository {
    this._cdaRepository ??= new CDARepository(new HttpCoreService());
    return this._cdaRepository;
  }

  get cdaUseCase(): ICDAUseCase {
    this._cdaUseCase ??= new CDAUseCase(this.cdaRepository);
    return this._cdaUseCase;
  }

  get connectorRepository(): IConnectorRepository {
    this._connectorRepository ??= new ConnectorRepository(
      new HttpCoreService(),
    );
    return this._connectorRepository;
  }

  get connectorUseCase(): IConnectorUseCase {
    this._connectorUseCase ??= new ConnectorUseCase(this.connectorRepository);
    return this._connectorUseCase;
  }

  get piRepository(): IPrincipalInvestigatorRepository {
    this._piRepository ??= new PrincipalInvestigatorRepository();
    return this._piRepository;
  }

  get investigatorUseCase(): IInvestigatorUseCase {
    this._investigatorUseCase ??= new InvestigatorUseCase(this.piRepository);
    return this._investigatorUseCase;
  }

  get rbqmRepository(): IRBQMRepository {
    this._rbqmRepository ??= new RBQMRepository();
    return this._rbqmRepository;
  }

  get rbqmUseCase(): IRBQMUseCase {
    this._rbqmUseCase ??= new RBQMUseCase(this.rbqmRepository);
    return this._rbqmUseCase;
  }

  get qmsRepository(): IQMSRepository {
    this._qmsRepository ??= new QMSRepository();
    return this._qmsRepository;
  }

  get qmsUseCase(): IQMSUseCase {
    this._qmsUseCase ??= new QMSUseCase(this.qmsRepository, {
      getStudyById: async (studyId) => {
        const study = await this.studyUseCase.getStudyById(studyId);
        return {
          id: study.id,
          title: study.title,
          protocolNumber: study.protocolNumber,
        };
      },
      getSitesByStudy: async (studyId) => {
        const sites = await this.siteUseCase.getSitesByStudy(studyId);
        return sites.map((site) => ({
          id: site.id,
          name: site.name,
          siteNumber: site.siteNumber,
        }));
      },
      getUsers: async () => {
        const users = await this.userUseCase.getUsers();
        return users.map((user) => ({
          id: user.id,
          name: user.name,
          role: user.studyRoles?.[0]?.role,
        }));
      },
    });
    return this._qmsUseCase;
  }
}

// Export singleton instance
export const container = DependencyContainer.getInstance();
